import React, { useRef, useState, useEffect } from 'react';
import { Box, Card, Stack, IconButton, Dropdown, MenuButton, MenuItem, Menu } from '@mui/joy';
import { FaCheck, FaEraser, FaPencilAlt } from 'react-icons/fa';
import { MdLineWeight } from 'react-icons/md';

function Frame({ setSelectedFiles, close, isMobile, currentLanguageData }) {
    const canvasRef = useRef(null);
    const contextRef = useRef(null);
    const [color, setColor] = useState('red');
    const [lineWidth, setLineWidth] = useState(2);

    useEffect(() => {
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        contextRef.current = context;

        const handleResize = () => {
            const container = canvas.parentElement;
            canvas.width = container.clientWidth;
            canvas.height = container.clientHeight;
        };

        handleResize();

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        const canvas = canvasRef.current;
        const context = contextRef.current;
        let drawing = false;

        const getTouchPos = (touch) => {
            const rect = canvas.getBoundingClientRect();
            return {
                x: touch.clientX - rect.left,
                y: touch.clientY - rect.top,
            };
        };

        const startDrawing = (e) => {
            drawing = true;
            draw(e);
        };

        const endDrawing = () => {
            drawing = false;
            context.beginPath();
        };

        const draw = (e) => {
            if (!drawing) return;
            const rect = canvas.getBoundingClientRect();

            let x, y;
            if (e.touches) {
                const touch = e.touches[0];
                ({ x, y } = getTouchPos(touch));
            } else {
                x = e.clientX - rect.left;
                y = e.clientY - rect.top;
            }

            context.lineWidth = lineWidth;
            context.lineCap = 'round';
            context.strokeStyle = color;
            context.lineTo(x, y);
            context.stroke();
            context.beginPath();
            context.moveTo(x, y);
        };

        canvas.addEventListener('mousedown', startDrawing);
        canvas.addEventListener('mousemove', draw);
        canvas.addEventListener('mouseup', endDrawing);
        canvas.addEventListener('mouseleave', endDrawing);

        canvas.addEventListener('touchstart', (e) => {
            e.preventDefault();
            startDrawing(e);
        });
        canvas.addEventListener('touchmove', (e) => {
            e.preventDefault();
            draw(e);
        });
        canvas.addEventListener('touchend', (e) => {
            e.preventDefault();
            endDrawing();
        });

        return () => {
            canvas.removeEventListener('mousedown', startDrawing);
            canvas.removeEventListener('mousemove', draw);
            canvas.removeEventListener('mouseup', endDrawing);
            canvas.removeEventListener('mouseleave', endDrawing);

            canvas.removeEventListener('touchstart', startDrawing);
            canvas.removeEventListener('touchmove', draw);
            canvas.removeEventListener('touchend', endDrawing);
        };
    }, [color, lineWidth]);


    const clearCanvas = () => {
        const canvas = canvasRef.current;
        const context = contextRef.current;
        context.clearRect(0, 0, canvas.width, canvas.height);
    };

    const saveCanvasAsImage = async () => {
        const canvas = canvasRef.current;
        const imageURL = canvas.toDataURL('image/png');

        const byteString = atob(imageURL.split(',')[1]);
        const arrayBuffer = new ArrayBuffer(byteString.length);
        const uint8Array = new Uint8Array(arrayBuffer);

        for (let i = 0; i < byteString.length; i++) {
            uint8Array[i] = byteString.charCodeAt(i);
        }

        const blob = new Blob([uint8Array], { type: 'image/png' });
        const file = new File([blob], 'frame-image.png', { type: 'image/png' });

        setSelectedFiles(prevFiles => [...prevFiles, file]);
        clearCanvas();
        close();
    };

    return (
        <div style={{ display: 'flex', flex: 1, justifyContent: 'center', position: 'relative', overflow: 'hidden', userSelect: 'none' }}>
            <Box style={{ width: '100%', height: '100%', position: 'relative' }}>
                <Card variant='soft' sx={{
                    position: 'absolute',
                    bottom: 20,
                    p: 1,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 10,
                    width: 'auto',
                    borderRadius: 80
                }}>
                    <Stack direction="row" spacing={1}>
                        {!isMobile && (
                            <Dropdown>
                                <MenuButton variant="soft" sx={{ pointerEvents: 'auto', p: 0, backgroundColor: 'transparent', aspectRatio: '1', height: '36px', borderRadius: '50%', marginLeft: -0.2 }}>
                                    <IconButton variant='soft' sx={{ width: '36px', height: '36px', borderRadius: 250 }}>
                                        <FaPencilAlt />
                                    </IconButton>
                                </MenuButton>
                                <Menu sx={{ zIndex: 99999, borderRadius: 12, py: 0 }} variant='plain'>
                                    <MenuItem onClick={() => setColor("black")}>{currentLanguageData.black}</MenuItem>
                                    <MenuItem onClick={() => setColor("white")}>{currentLanguageData.white}</MenuItem>
                                    <MenuItem onClick={() => setColor("red")}>{currentLanguageData.red}</MenuItem>
                                    <MenuItem onClick={() => setColor("blue")}>{currentLanguageData.blue}</MenuItem>
                                    <MenuItem onClick={() => setColor("green")}>{currentLanguageData.green}</MenuItem>
                                </Menu>
                            </Dropdown>
                        )}
                        {!isMobile && (
                            <Dropdown>
                                <MenuButton variant="soft" sx={{ pointerEvents: 'auto', p: 0, backgroundColor: 'transparent', aspectRatio: '1', height: '36px', borderRadius: '50%', marginLeft: -0.2 }}>
                                    <IconButton variant='soft' sx={{ width: '36px', height: '36px', borderRadius: 250 }}>
                                        <MdLineWeight />
                                    </IconButton>
                                </MenuButton>
                                <Menu sx={{ zIndex: 99999, borderRadius: 12, py: 0 }} variant='plain'>
                                    <MenuItem onClick={() => setLineWidth(2)}>{currentLanguageData.thin}</MenuItem>
                                    <MenuItem onClick={() => setLineWidth(6)}>{currentLanguageData.medium}</MenuItem>
                                    <MenuItem onClick={() => setLineWidth(20)}>{currentLanguageData.fat}</MenuItem>
                                </Menu>
                            </Dropdown>
                        )}
                        <IconButton variant='soft' sx={{ width: '36px', height: '36px', borderRadius: 250 }} onClick={() => clearCanvas()}>
                            <FaEraser />
                        </IconButton>
                        <IconButton color='success' variant='plain' sx={{ width: '36px', height: '36px', borderRadius: 250 }} onClick={saveCanvasAsImage}>
                            <FaCheck />
                        </IconButton>
                    </Stack>
                </Card>

                <canvas style={{ pointerEvents: 'auto' }} ref={canvasRef} />
            </Box>
        </div>
    );
}

export default Frame;
